"use client";
import { Role } from "../role/role.js";
import { t as getBasePlacement } from "../__chunks/B3k5_R1L.js";
import { usePopoverDisclosure } from "../popover/popover-disclosure.js";
import { useCompositeTypeahead } from "../composite/composite-typeahead.js";
import { useHovercardAnchor } from "../hovercard/hovercard-anchor.js";
import { MenuContextProvider, useMenuProviderContext } from "./menu-context.js";
import { createElement, createHook, forwardRef, useEvent, useId, useMergeRefs, useWrapElement } from "@ariakit/react-utils";
import { disabledFromElement, disabledFromProps, getPopupItemRole, getPopupRole, invariant } from "@ariakit/utils";
import { useRef } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/menu/menu-button.tsx
const TagName = "button";
function getInitialFocus(event, dir) {
	return {
		ArrowDown: dir === "bottom" || dir === "top" ? "first" : false,
		ArrowUp: dir === "bottom" || dir === "top" ? "last" : false,
		ArrowRight: dir === "right" ? "first" : false,
		ArrowLeft: dir === "left" ? "first" : false
	}[event.key];
}
function hasActiveItem(items, excludeElement) {
	return !!items?.some((item) => {
		if (!item.element) return false;
		if (item.element === excludeElement) return false;
		return item.element.getAttribute("aria-expanded") === "true";
	});
}
/**
* Returns props to create a `MenuButton` component.
* @see https://ariakit.com/components/menu
* @example
* ```jsx
* const store = useMenuStore();
* const props = useMenuButton({ store });
* <Role {...props}>Edit</Role>
* <Menu store={store}>
*   <MenuItem>Undo</MenuItem>
*   <MenuItem>Redo</MenuItem>
* </Menu>
* ```
*/
const useMenuButton = createHook(function useMenuButton({ store, focusable, accessibleWhenDisabled, showOnHover, ...props }) {
	const context = useMenuProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "MenuButton must receive a `store` prop or be wrapped in a MenuProvider component.");
	const ref = useRef(null);
	const parentMenu = store.parent;
	const parentMenubar = store.menubar;
	const hasParentMenu = !!parentMenu;
	const parentIsMenubar = !!parentMenubar && !hasParentMenu;
	const disabled = disabledFromProps(props);
	const isDisabled = (element) => disabled || disabledFromElement(element);
	const showMenu = () => {
		const trigger = ref.current;
		if (!trigger) return;
		store?.setDisclosureElement(trigger);
		store?.setAnchorElement(trigger);
		store?.show();
	};
	const onFocusProp = props.onFocus;
	const onFocus = useEvent((event) => {
		onFocusProp?.(event);
		if (isDisabled(event.currentTarget)) return;
		if (event.defaultPrevented) return;
		store?.setAutoFocusOnShow(false);
		store?.setActiveId(null);
		if (!parentMenubar) return;
		if (!parentIsMenubar) return;
		const { items } = parentMenubar.getState();
		if (hasActiveItem(items, event.currentTarget)) showMenu();
	});
	const dir = useStoreState(store, (state) => getBasePlacement(state.placement));
	const onKeyDownProp = props.onKeyDown;
	const onKeyDown = useEvent((event) => {
		onKeyDownProp?.(event);
		if (isDisabled(event.currentTarget)) return;
		if (event.defaultPrevented) return;
		const initialFocus = getInitialFocus(event, dir);
		if (initialFocus) {
			event.preventDefault();
			const { open } = store.getState();
			if (open) {
				const id = initialFocus === "last" ? store.last() : store.first();
				store.move(id);
				return;
			}
			showMenu();
			store?.setAutoFocusOnShow(true);
			store?.setInitialFocus(initialFocus);
		}
	});
	const onClickProp = props.onClick;
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		if (!store) return;
		const isKeyboardClick = !event.detail;
		const { open } = store.getState();
		if (!open || isKeyboardClick) {
			if (!hasParentMenu || isKeyboardClick) store.setAutoFocusOnShow(true);
			store.setInitialFocus(isKeyboardClick ? "first" : "container");
		}
		if (hasParentMenu) showMenu();
	});
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(MenuContextProvider, {
		value: store,
		children: element
	}), [store]);
	if (hasParentMenu) props = {
		...props,
		render: /* @__PURE__ */ jsx(Role.div, { render: props.render })
	};
	const id = useId(props.id);
	const parentContentElement = useStoreState(parentMenu?.combobox || parentMenu, "contentElement");
	props = {
		role: hasParentMenu || parentIsMenubar ? getPopupItemRole(parentContentElement, "menuitem") : void 0,
		"aria-haspopup": getPopupRole(useStoreState(store, "contentElement"), !!store.combobox ? "dialog" : "menu"),
		...props,
		id,
		ref: useMergeRefs(ref, props.ref),
		onFocus,
		onKeyDown,
		onClick
	};
	props = useHovercardAnchor({
		store,
		focusable,
		accessibleWhenDisabled,
		...props,
		showOnHover: (event) => {
			const getShowOnHover = () => {
				if (typeof showOnHover === "function") return showOnHover(event);
				if (showOnHover != null) return showOnHover;
				if (hasParentMenu) return true;
				if (!parentMenubar) return false;
				const { items } = parentMenubar.getState();
				return parentIsMenubar && hasActiveItem(items);
			};
			if (!getShowOnHover()) return false;
			const parent = parentIsMenubar ? parentMenubar : parentMenu;
			if (!parent) return true;
			parent.setActiveId(event.currentTarget.id);
			return true;
		}
	});
	props = usePopoverDisclosure({
		store,
		toggleOnClick: !hasParentMenu,
		focusable,
		accessibleWhenDisabled,
		...props
	});
	props = useCompositeTypeahead({
		store,
		typeahead: parentIsMenubar,
		...props
	});
	return props;
});
/**
* Renders a menu button that toggles the visibility of a
* [`Menu`](https://ariakit.com/reference/menu) component when clicked or when
* using arrow keys.
* @see https://ariakit.com/components/menu
* @example
* ```jsx {2}
* <MenuProvider>
*   <MenuButton>Edit</MenuButton>
*   <Menu>
*     <MenuItem>Undo</MenuItem>
*     <MenuItem>Redo</MenuItem>
*   </Menu>
* </MenuProvider>
* ```
*/
const MenuButton = forwardRef(function MenuButton(props) {
	return createElement(TagName, useMenuButton(props));
});
//#endregion
export { MenuButton, useMenuButton };

//# sourceMappingURL=menu-button.js.map